import Icon from '../ui/Icon';
import Button from '../ui/Button';
import StatusBadge from '../ui/StatusBadge';
import { formatAmsterdam } from './amsterdamTime';
import { formatPrice, statusMeta } from './scanAdapter';

const spinner = <Icon name="progress_activity" size={18} className="animate-spin" />;

/** What happened to a scan that's no longer pending. */
export function ReviewSummary({ scan }) {
  if (scan.status === 'approved') {
    const applied =
      scan.priceApplied === true ? 'Station price updated' : scan.priceApplied === false ? 'Not applied: the station had a newer price' : null;
    return (
      <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-on-surface" data-testid="review-summary">
        <Icon name="verified" size={18} className="text-secondary" />
        Approved by {scan.reviewedBy?.name || 'an admin'} on <span className="font-data-mono">{formatAmsterdam(scan.reviewedAt)}</span>
        {applied && <span className={`text-xs ${scan.priceApplied ? 'text-secondary' : 'text-status-pending-text'}`}>· {applied}</span>}
      </p>
    );
  }
  if (scan.status === 'rejected') {
    return (
      <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-on-surface" data-testid="review-summary">
        <Icon name="block" size={18} className="text-error" />
        Rejected: {scan.rejectReason || 'no reason given'}
        {scan.reviewedBy?.name && (
          <span className="text-xs text-on-surface-variant">
            · by {scan.reviewedBy.name}, <span className="font-data-mono">{formatAmsterdam(scan.reviewedAt)}</span>
          </span>
        )}
      </p>
    );
  }
  return (
    <p className="flex items-center gap-2 text-sm text-on-surface-variant" data-testid="review-summary">
      <Icon name="edit_document" size={18} />
      Draft: the user hasn&apos;t submitted this scan yet.
    </p>
  );
}

/**
 * Back, scan title, status and the review actions (pending only).
 * `busy` is the running action ('save' | 'approve' | 'reject') or null.
 */
export default function ScanHeader({ scan, dirty, busy, formPrice, onBack, onReject, onSave, onApprove }) {
  const status = statusMeta(scan.status);
  const pending = scan.status === 'pending';
  const disabled = Boolean(busy);

  return (
    <section className="bg-surface-container-lowest rounded-xl border border-outline-variant/30 shadow-xs px-5 py-4 flex flex-col xl:flex-row xl:items-center justify-between gap-4">
      <div className="flex flex-wrap items-center gap-3 min-w-0">
        <button
          type="button"
          onClick={onBack}
          disabled={disabled}
          aria-label="Back"
          title="Back"
          className="w-9 h-9 rounded-lg flex items-center justify-center bg-surface-container-low text-on-surface hover:bg-surface-container disabled:opacity-40 disabled:cursor-not-allowed outline-none focus-visible:ring-2 focus-visible:ring-status-ok-dot"
        >
          <Icon name="arrow_back" size={20} />
        </button>
        <h1 className="flex items-center gap-2 text-base font-bold text-primary">
          <Icon name="receipt_long" size={20} className="text-on-surface-variant" />
          Scan #{scan.id}
        </h1>
        <StatusBadge tone={status.tone}>{status.label}</StatusBadge>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {pending ? (
          <>
            <Button
              variant="danger"
              size="lg"
              icon={busy === 'reject' ? undefined : 'close'}
              onClick={onReject}
              disabled={disabled}
              className="border border-error/40"
            >
              {busy === 'reject' && spinner}
              Reject Scan
            </Button>
            <Button variant="muted" size="lg" icon={busy === 'save' ? undefined : 'save'} onClick={onSave} disabled={disabled || !dirty}>
              {busy === 'save' && spinner}
              {busy === 'save' ? 'Saving…' : 'Save Edits'}
            </Button>
            <Button size="lg" icon={busy === 'approve' ? undefined : 'verified'} onClick={onApprove} disabled={disabled}>
              {busy === 'approve' && spinner}
              Approve{formPrice !== null ? ` (${formatPrice(formPrice)}/L)` : ''}
            </Button>
          </>
        ) : (
          <ReviewSummary scan={scan} />
        )}
      </div>
    </section>
  );
}
